import { BodyText, Label, Link } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import {
  Avatar,
  Badge,
  FlexColumn,
  FlexRow,
  formatHash,
  SvgIcon,
  TruncateBox,
  VerifiedIcon,
} from '@make-software/cspr-design';
import styled, { useTheme } from 'styled-components';
import { ContractTypeName } from '../contract-type/contract-type-name';
import { ContractTypeId } from '@block-explorer/entities';
import { AccountSearchResult } from '../../hooks/search-utils';
import { buildRouterPath, useCustomHistory } from '../../router/utils';
import {
  ACCOUNT_DETAILS_PATH,
  CONTRACT_PACKAGE_PATH,
} from '../../router/paths';
import ContractIcon from '../contract-icon/contract-icon';

const StyledIcon = styled(SvgIcon)(({ theme }) =>
  theme.withMedia({
    cursor: 'pointer',
    path: {
      fill: theme.styleguideColors.contentRed,
    },
  })
);

export const SearchDropdownItems = ({
  account,
  accountInfoLoading,
  onSelect,
}: {
  account: AccountSearchResult;
  accountInfoLoading?: boolean;
  onSelect: () => void;
}) => {
  const {
    publicKey,
    accountHash,
    logo,
    isVerified,
    isKnownOwner,
    isValidator,
    url,
    name,
  } = account;
  const { t } = useTranslation();
  const history = useCustomHistory();

  const handleClick = (event) => {
    event.stopPropagation();

    if (!accountHash) {
      return;
    }

    const path = buildRouterPath(
      ACCOUNT_DETAILS_PATH,
      { id: publicKey || accountHash },
      undefined
    );

    onSelect();
    history.push(path);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleClick(event);
    }
  };

  return (
    <FlexRow
      grow={1}
      gap={8}
      align={'center'}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <FlexColumn>
        {logo ? (
          <Avatar
            src={logo}
            size="semiMedium"
            loading={accountInfoLoading}
            alt={'Account logo'}
          />
        ) : (
          <Avatar
            hash={publicKey || accountHash}
            size="semiMedium"
            loading={accountInfoLoading}
          />
        )}
      </FlexColumn>
      <FlexColumn style={{ width: '100%' }} gap={2}>
        {name ? (
          <FlexRow align={'center'}>
            <BodyText scale={'sm'} size={1}>
              {name}
            </BodyText>
            {isVerified && (
              <SvgIcon
                src={VerifiedIcon}
                role={'img'}
                alt={'Verified icon'}
                marginLeft
              />
            )}
            {isKnownOwner && (
              <StyledIcon
                src={VerifiedIcon}
                role={'img'}
                alt={'Verified icon'}
                marginLeft
              />
            )}
          </FlexRow>
        ) : null}
        <FlexRow align={'center'}>
          <BodyText size={3} scale={'xs'} variation={'darkGray'} monotype>
            {publicKey ? formatHash(publicKey) : formatHash(accountHash)}
          </BodyText>
        </FlexRow>
        <FlexRow align={'center'}>
          {url && (
            <Link
              color="primaryBlue"
              href={url}
              rel="noopener"
              onClick={(e) => e.stopPropagation()}
            >
              <TruncateBox size={1.6}>
                <BodyText size={3} scale={'sm'} noWrap>
                  {url}
                </BodyText>
              </TruncateBox>
            </Link>
          )}
        </FlexRow>
      </FlexColumn>
      <FlexColumn>
        {isValidator ? (
          <Badge
            variation={'green'}
            lineHeight={'xxs'}
            label={
              <Label variation="white">
                <Trans t={t}>Validator</Trans>
              </Label>
            }
          />
        ) : null}
      </FlexColumn>
    </FlexRow>
  );
};

export const SearchDropdownContract = ({
  isFeatured,
  latestVersionContractTypeId,
  logo,
  websiteUrl,
  hash,
  contractName,
  accountInfoLoading,
  onSelect,
}) => {
  const history = useCustomHistory();
  const theme = useTheme();

  const handleClick = (event) => {
    event.stopPropagation();

    if (!hash) {
      return;
    }

    const path = buildRouterPath(
      CONTRACT_PACKAGE_PATH,
      { id: hash },
      undefined
    );

    onSelect();
    history.push(path);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleClick(event);
    }
  };

  const badgeColor = {
    [ContractTypeId.System]: 'blue',
    [ContractTypeId.Cep18]: theme.styleguideColors.contentYellow,
    [ContractTypeId.CustomCep18]: theme.styleguideColors.contentYellow,
    [ContractTypeId.CEP47Nft]: 'violet',
    [ContractTypeId.CustomCEP47Nft]: 'violet',
    [ContractTypeId.DeFi]: 'blue',
    [ContractTypeId.CEP78Nft]: 'violet',
    [ContractTypeId.CustomCEP78Nft]: 'violet',
    [ContractTypeId.NFTMarketplace]: 'violet',
    [ContractTypeId.CEP95Nft]: 'violet',
  };

  return (
    <FlexRow
      tabIndex={0}
      grow={1}
      gap={8}
      align={'center'}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <FlexColumn>
        <ContractIcon
          size={'semiMedium'}
          contractTypeId={latestVersionContractTypeId}
          src={logo}
          loading={accountInfoLoading}
        />
      </FlexColumn>
      <FlexColumn style={{ width: '100%' }} gap={2}>
        <FlexRow align={'center'}>
          <BodyText scale={'sm'} size={1}>
            {contractName}
          </BodyText>
          {isFeatured && (
            <StyledIcon
              src={VerifiedIcon}
              role={'img'}
              alt={'Verified icon'}
              marginLeft
            />
          )}
        </FlexRow>
        <FlexRow align={'center'}>
          <BodyText size={3} scale={'xs'} variation={'darkGray'} monotype>
            {formatHash(hash)}
          </BodyText>
        </FlexRow>
        <FlexRow align={'center'}>
          {websiteUrl && (
            <Link
              color="primaryBlue"
              href={websiteUrl}
              rel="noopener"
              onClick={(e) => e.stopPropagation()}
            >
              <TruncateBox size={1.6}>
                <BodyText size={3} scale={'sm'} noWrap>
                  {websiteUrl}
                </BodyText>
              </TruncateBox>
            </Link>
          )}
        </FlexRow>
      </FlexColumn>
      <FlexColumn>
        {latestVersionContractTypeId ? (
          <Badge
            variation={badgeColor[latestVersionContractTypeId]}
            lineHeight={'xxs'}
            label={
              <Label lineHeight={'xs'}>
                <ContractTypeName
                  short
                  contractTypeId={latestVersionContractTypeId ?? null}
                />
              </Label>
            }
          />
        ) : null}
      </FlexColumn>
    </FlexRow>
  );
};
